<template>
    <v-row
        no-gutters
        class="pr-8 pl-6 pt-8"
    >
        <v-col cols="12">
            <AppStoreListHeader
                :item-count="account_custom_fields.length"
                :item-label="$t('Task fields')"
                :add-label="$t('Add')"
                :show-button="appStoreDialogFlags.canManageAccount()"
                @add-item="editCustomField(0)"
            />
            <template v-if="account_custom_fields.length > 0">
                <v-row no-gutters>
                    <v-col cols="12">
                        <data-table
                            :items="account_custom_fields"
                            :headers="headers"
                            :loading="loader.isLoading"
                            :row-clickable="appStoreDialogFlags.canManageAccount()"
                            @click:row="editCustomField($event.id)"
                        >
                            <template v-slot:item.title="{ item }">
                                <div class="d-flex align-center">
                                    <span class="mr-1">
                                        {{ item.title }}
                                    </span>
                                    <icon v-if="!item.billable && ['price', 'number'].includes(item.type)" size="20" icon="non_billable"/>
                                </div>
                            </template>

                            <template v-slot:item.is_monitor="{ item }">
                                <div class="d-flex align-center">
                                    <span class="mr-1">{{ item.is_monitor === 1 ? $t('Yes') : '' }}</span>
                                </div>
                            </template>
                        </data-table>
                    </v-col>
                </v-row>
            </template>
            <v-row class="ml-0">
                <secondary-button color="primary" @click="viewArchiveCustomFields">
                    {{ $t('View archive') }}
                </secondary-button>
            </v-row>
        </v-col>
        <CustomFieldEditDialog
            v-if="dialog.show"
            :show-dialog="dialog.show"
            :account-id="selectedAccount.id"
            :custom-field-id="dialog.id"
            :related-type="CUSTOM_FIELD_RELATED_TYPE.TASK"
            @close="closeFieldDialog"
        ></CustomFieldEditDialog>
    </v-row>
</template>

<script>
import {ref, computed, onMounted, watch, reactive} from "@vue/composition-api";
import {$t} from "@/i18n";
import AppModule from "../../../store/modules/app";
import CustomFieldEditDialog from "@/components/Dialog/CustomFieldEditDialog/Index";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import AppStoreListHeader from "../../../components/AppStore/AppStoreListHeader";
import Loader from "../../../utility/loader";
import {CUSTOM_FIELD_RELATED_TYPE} from "@/helpers/constants";
import ApiBillingCustomFields from "@/api/billing/customFields";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import DialogModule from "@/store/modules/dialog";
import {FEATURE_CUSTOM_FIELDS, validateFeatureAllowed} from "@/modules/subscription/allowed_features";
import AccountModule from "@/store/modules/account";


export default {
    name: "TasksDialogCustomFieldsTab",
    components: {CustomFieldEditDialog, AppStoreListHeader},
    setup(props) {
        const dialog = ref({show: false, id: 0});
        const loader = reactive(new Loader())

        const {fetchClassifiers, custom_fields} = AppModule();
        const {setDialogOptions, openDialog, setConfirmOptions, openConfirmDialog} = DialogModule();
        const {appStoreDialogFlags, appStoreDialogGetters} = appStoreDialog()
        const {isFeatureEnabled} = AccountModule();
        const selectedAccount = computed(() => {
            return appStoreDialogGetters.getSelectedCompany()
        })

        const fetchData = function () {
            loader.withLoader(async () => {
                fetchClassifiers({
                    types: ["custom_fields"],
                    account_id: selectedAccount.value.id,
                    force: {default: true, types: ["custom_fields"]}
                })
            })
        }

        const account_custom_fields = computed(() => {
            let account_custom_fields = custom_fields.value[selectedAccount.value.id]
            return account_custom_fields === undefined ? [] : account_custom_fields.filter(x => x.related_type === 'task');
        });

        const isMonitoringEnabled = computed(() => isFeatureEnabled(selectedAccount.value.id, 'monitoring', false))

        const headers = computed(() => {
            let headers = [
                {text: $t("Name"), value: "title", sortable: true},
                {text: $t("Type"), value: "type", sortable: true},
            ]
            if (isMonitoringEnabled.value) {
                headers.push({text: $t("Monitoring"), value: "is_monitor", sortable: true})
            }
            if (appStoreDialogFlags.canManageAccount()) {
                headers.push({text: "", value: "edit", sortable: false})
            }
            return headers
        });

        const editCustomField = id => {
            validateFeatureAllowed(FEATURE_CUSTOM_FIELDS, selectedAccount.value)
            if (!appStoreDialogFlags.canManageAccount()) {
                return
            }
            dialog.value.id = id;
            dialog.value.show = true;
        };

        const closeFieldDialog = () => {
            dialog.value.show = false;
            dialog.value.id = 0;
        };

        const viewArchiveCustomFields = async () => {
            try {
                const data = await ApiBillingCustomFields.getArchivedCustomFields(selectedAccount.value.id)
                const archivedCustomFields = data.custom_fields

                setConfirmOptions({
                    title: $t("Restore from archive"),
                    options: archivedCustomFields.map(function (x) {
                        return {name: x.id, text: x.title, checked: false};
                    }),
                    text: (archivedCustomFields.length < 1 && $t("Empty list")) || null,
                    confirm_text: $t("Restore"),
                    cancel_text: $t("Cancel"),
                    action: async (selected) => {
                        if (selected.length) {
                            await ApiBillingCustomFields.restoreArchivedCustomFields(selectedAccount.value.id, selected)
                            await fetchData()
                        }
                    }
                });

                openConfirmDialog();
            } catch (ex) {
                errorDialog.showError(ex)
            }
        };

        fetchData()

        return {
            account_custom_fields,
            editCustomField,
            headers,
            dialog,
            loader,
            closeFieldDialog,
            appStoreDialogFlags,
            selectedAccount,
            CUSTOM_FIELD_RELATED_TYPE,
            viewArchiveCustomFields
        };
    }
};
</script>
